// Small floating popover that surfaces a frame node's prebuilt probe questions.
// Triggered by clicking a MECE-banner gap chip in CardsPanel. Each probe is a
// suggested question the user can hand to the agent with one click.
//
// This is the spiritual successor to OpsPanel's `gap` mode, lifted out of the
// fourth column so that column can be reclaimed by the GapLedger (its
// designed role per uc-02 prototype). Frame gaps will eventually move into
// the unified GapLedger as a "frame gaps" group, but for now this preserves
// the existing flow with a lighter UI.
//
// Closes on: × button, Esc, click outside, or after a probe is fired.

import { useEffect, useRef } from 'react';
import type { FrameNode } from '../../api';

export interface GapProbePopoverProps {
  node: FrameNode;
  onClose: () => void;
  onAskAgent: (text: string) => void;
}

const POPOVER_WIDTH = 460;
const VIEWPORT_PAD = 16;

export function GapProbePopover({ node, onClose, onAskAgent }: GapProbePopoverProps) {
  const ref = useRef<HTMLDivElement>(null);
  const probes = node.probes ?? [];

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopImmediatePropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      const el = ref.current;
      if (!el) return;
      if (e.target instanceof Node && el.contains(e.target)) return;
      onClose();
    };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [onClose]);

  return (
    <div
      ref={ref}
      role="dialog"
      aria-modal="false"
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'fixed',
        left: '50%',
        top: '50%',
        transform: 'translate(-50%, -50%)',
        width: POPOVER_WIDTH,
        maxHeight: `calc(100vh - ${VIEWPORT_PAD * 2}px)`,
        background: 'white',
        border: '1px solid var(--rule)',
        borderLeft: '3px solid var(--amber)',
        borderRadius: 6,
        boxShadow: '0 16px 48px rgba(0,0,0,0.18), 0 2px 6px rgba(0,0,0,0.06)',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1500,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: '10px 12px 8px 14px',
          borderBottom: '1px solid var(--rule)',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          background: 'var(--paper-0)',
          flexShrink: 0,
        }}
      >
        <span
          className="mono"
          style={{ fontSize: 10, color: 'var(--amber)', letterSpacing: '.12em', fontWeight: 700 }}
        >
          GAP · {node.path}
        </span>
        <span style={{ fontSize: 11.5, color: 'var(--ink-3)', fontStyle: 'italic' }}>
          {node.label}
        </span>
        <span style={{ flex: 1 }} />
        <button
          onClick={onClose}
          aria-label="Close"
          className="mono"
          style={{
            fontSize: 14,
            lineHeight: 1,
            padding: '2px 6px',
            border: 'none',
            background: 'transparent',
            color: 'var(--ink-3)',
            cursor: 'pointer',
          }}
        >
          ×
        </button>
      </div>

      <div style={{ padding: '16px 18px', flex: 1, overflowY: 'auto' }}>
        <div className="serif" style={{ fontSize: 17, fontWeight: 500, color: 'var(--ink-0)', marginBottom: 6 }}>
          {node.label} is empty.
        </div>
        {node.description && (
          <div style={{ fontSize: 12.5, color: 'var(--ink-2)', fontStyle: 'italic', marginBottom: 14, lineHeight: 1.55 }}>
            {node.description}
          </div>
        )}
        {probes.length === 0 ? (
          <div style={{ fontSize: 12, color: 'var(--ink-3)', fontStyle: 'italic' }}>
            No prebuilt probes for this node yet. Ask the agent to explore this with you.
          </div>
        ) : (
          <>
            <div
              className="mono"
              style={{
                fontSize: 10, letterSpacing: '.1em', fontWeight: 700,
                color: 'var(--ink-3)', marginBottom: 10,
              }}
            >
              SUGGESTED QUESTIONS
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {probes.map((q, i) => (
                <button
                  key={i}
                  onClick={() => {
                    onAskAgent(q);
                    onClose();
                  }}
                  style={{
                    textAlign: 'left',
                    padding: '10px 12px',
                    background: 'var(--paper-0)',
                    border: '1px solid var(--rule)',
                    borderLeft: '3px solid var(--amber)',
                    borderRadius: 4,
                    fontSize: 13,
                    lineHeight: 1.5,
                    color: 'var(--ink-0)',
                    cursor: 'pointer',
                  }}
                >
                  {q}
                  <div
                    className="mono"
                    style={{
                      fontSize: 9.5,
                      color: 'var(--ink-3)',
                      letterSpacing: '.04em',
                      marginTop: 5,
                    }}
                  >
                    → ask agent
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
